import { useCallback, useMemo, useState } from 'react';
import filter from 'lodash/filter';
import size from 'lodash/size';

import {
  CategoryID,
  FetchCategoriesScopes,
  FetchCategoriesSortTypes
} from '../../../../../../categories/categoriesTypes';
import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../products/productsTypes';
import { IconsEnum } from '../../../../../../../assets/icons/types';

import {
  FETCH_CATEGORIES_SELECT_FIELD_QUERY,
  FetchCategoriesSelectFieldQueryResponse
} from '../../../../../../categories/queries/fetchCategoriesSelectField.query';
import { CACHE_FETCH_CATEGORIES_SELECT_FIELD_QUERY } from '../../../../../../categories/queries/fetchCacheCategoriesSelectField.query';

import { useFinPaginatedCategories } from '../../../../../../categories/hooks/useFinPaginatedCategories';

import { ThreeDStockProductsFiltersRenderTypeFilterItem } from './components/ThreeDStockProductsFiltersRenderTypeFilterItem';

import { ButtonHelper } from '../../../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../../../helpers/Icon';
import { Translate } from '../../../../../../../helpers/Translate';

import {
  CategoriesPermissions,
  productCategoriesScopes
} from '../../../../../../categories/categoriesConstants';

import { CategoryCache } from '../../../../../../categories/CategoryCache';

import { categoriesKeys } from '../../../../../../../locales/keys';

const scope = FetchCategoriesScopes.FACILITIES_RENDER_TYPES;

const options = {
  staleTime: 1000 * 60 * 60,
  withoutPrefetch: true
};

const fieldName = FetchProductsFilterFields.RENDER_TYPE_IDS;

interface ThreeDStockProductsFiltersRenderTypeFilterProps {
  productsFilters: FetchProductsFilters;
  changeProductsFilters: ChangeProductsFiltersFunc;
}

function ThreeDStockProductsFiltersRenderTypeFilter({
  changeProductsFilters,
  productsFilters
}: ThreeDStockProductsFiltersRenderTypeFilterProps) {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const toggleIsOpen = useCallback<() => void>(
    () => setIsOpen((prevState) => !prevState),
    []
  );

  const productCategoriesAction = productCategoriesScopes.includes(scope)
    ? CategoriesPermissions.READ_PRODUCT_CATEGORIES_INDEX_ES_QUERY
    : undefined;

  const { categories } =
    useFinPaginatedCategories<FetchCategoriesSelectFieldQueryResponse>({
      action: productCategoriesAction,
      cacheKey: CategoryCache.categoriesSelectFieldCacheKey(scope),
      query: FETCH_CATEGORIES_SELECT_FIELD_QUERY,
      cacheQuery: CACHE_FETCH_CATEGORIES_SELECT_FIELD_QUERY,
      initialFilters: { scope: { eq: scope } },
      initialSort: [FetchCategoriesSortTypes.LOCALIZED_NAME_ASC],
      initialLimit: 1000,
      options
    });

  const checkedCategories = useMemo(() => {
    const value = productsFilters[fieldName];
    return Array.isArray(value) ? value : [];
  }, [productsFilters]);

  const handleCheckedTag = useCallback(
    (categoryId: CategoryID, checked: boolean) => {
      const newCategoriesIds = checked
        ? [...checkedCategories, categoryId]
        : filter(
            checkedCategories,
            (checkedCategoryId) => checkedCategoryId !== categoryId
          );

      changeProductsFilters(
        {
          [fieldName]: newCategoriesIds
        },
        size(newCategoriesIds) > 0 ? [] : [fieldName]
      );
    },
    [changeProductsFilters, checkedCategories]
  );

  if (size(categories) === 0) {
    return null;
  }

  return (
    <div className="p-4">
      <div className="flex flex-col gap-3">
        <ButtonHelper
          className="flex items-center gap-2 rounded-sm"
          dataGa="render-type-filter-toggle"
          onClick={toggleIsOpen}
        >
          <span className="text-sm font-semibold text-left flex-1">
            <Translate id={categoriesKeys.renderer} />
          </span>

          <Icon
            icon={isOpen ? IconsEnum.CHEVRON_DOWN_SOLID : IconsEnum.CHEVRON_UP}
            className="h-5 w-5 text-gray-500"
          />
        </ButtonHelper>

        {isOpen && (
          <div>
            <div className="flex flex-wrap gap-2">
              {categories?.map((category) => (
                <ThreeDStockProductsFiltersRenderTypeFilterItem
                  key={category.id}
                  category={category}
                  checked={checkedCategories.includes(category.id)}
                  onCheckedCategory={handleCheckedTag}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ThreeDStockProductsFiltersRenderTypeFilter;
